'use client';

import { notFound } from 'next/navigation';
import { parseDate } from '@frontend/jetlend-core/src/formatters/formatUtils';
import parse from 'html-react-parser';
import Link from 'next/link';
import Section from '@app/ui/Section/Section';
import ApplySection from '@app/components/ApplySection/ApplySection';
import { ClientType } from '@app/models/common/common';
import SectionPart from '@app/ui/SectionPart/SectionPart';
import styles from './BlogBody.module.scss';
import WpPostNavigationMobile from '@app/components/WpPostNavigation/WpPostNavigationMobile';
import WpPostNavigation from '@app/components/WpPostNavigation/WpPostNavigation';
import Image from '@app/components/Image/Image';
import WpPostContent from '@app/components/WpPostContent/WpPostContent';
import SocialButtonsList from '@app/components/SociaButtonsList/SocialButtonsList';
import NewsPostRecommendationsSection from '@app/features/news/NewsPostRecommendationsSection/NewsPostRecommendationsSection';
import { formatTextWithNumbers } from '@app/utils/formatTextWithNumbers';
import {
    checkContentEmpty,
    options,
} from '@app/components/WpPostNavigation/WpPostNavigation.logic';

export interface IProps {
    /**
     * Содержимое поста
     */
    post: Record<string, any>;
    /**
     * slug категории
     */
    categorySlug: string;
}

/**
 * Функция для получения url категории по ее slug
 * @param categorySlug - slug категории
 */
function getCategoryUrl(categorySlug: string) {
    switch (categorySlug) {
    case 'blog':
        return '/blog';
    case 'academy':
        return '/academy/articles';
    default:
        return '/';
    }
}

export default function BlogBody({
    post,
    categorySlug,
}: IProps) {
    const {
        category,
        content,
        date,
        title,
        preview_url,
        tags,
        recommendations,
    } = post;

    // Ensure that news post opening in the same category slug
    if (category && category.slug !== categorySlug) {
        return notFound();
    }

    const categoryUrl = getCategoryUrl(categorySlug);
    const postDate = parseDate(date).format('DD / MM / YYYY');
    const postTitle = parse(formatTextWithNumbers(title));

    const parsedContent = parse(content, options);
    const isContentEmpty = checkContentEmpty(parsedContent);

    return (
        <div>
            <Section
                animation="none"
                footer={(
                    <ApplySection sectionType="post-footer" type={ClientType.INVESTOR} />
                )}
            >
                <SectionPart className={styles['post']}>
                    <WpPostNavigationMobile content={content} />
                    <div className={styles['post__path']}>
                        <Link href={categoryUrl} className={styles['post__path-link']}>
                            {category.title}
                        </Link>
                        <span>
                            {'>'}
                        </span>
                        <span className={styles['post__path-item']}>
                            {postTitle}
                        </span>
                    </div>
                    <div
                        className={styles['post__wrapper']}
                        style={{
                            gridTemplateColumns: isContentEmpty ? '1fr 5fr 1fr' : '2fr 5fr 1fr',
                        }}
                    >
                        {!isContentEmpty && <WpPostNavigation parsedContent={parsedContent} />}
                        <div className={styles['post__container']}>
                            <div className={styles['post__image-wrapper']}>
                                <Image
                                    src={preview_url}
                                    alt=""
                                    fill
                                    className={styles['post__image']}
                                />
                            </div>
                            <h1 className={styles['post__title']}>
                                {postTitle}
                            </h1>
                            <WpPostContent
                                content={content}
                                contentClassName={styles['post__content']}
                            />
                            <div className={styles['post__footer']}>
                                <div className={styles['post__tags-container']}>
                                    <SocialButtonsList className={styles['social-media--horizontal']} align="horizontal" />
                                    {!!tags.length && (
                                        <p className={styles['post__tags']}>
                                            Теги:
                                            {tags.map(tag => (
                                                <Link href={`/tag/${tag.slug}/`} key={tag.id}>#{tag.title}</Link>
                                            ))}
                                        </p>
                                    )}
                                </div>
                                <p className={styles['post__date']}>
                                    {postDate}
                                </p>
                            </div>
                        </div>
                        <SocialButtonsList className={styles['social-media']} />
                    </div>
                </SectionPart>
            </Section>
            {recommendations && recommendations.length > 0 && (
                <NewsPostRecommendationsSection recommendations={recommendations} />
            )}
        </div>
    );
}
